import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Label from './Label.jsx';
import { MuiDateRangePicker } from "./mui-daterange-picker";
import { ThemeProvider } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers-v5/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers-v5/LocalizationProvider';
import { backYouTheme } from '../../../../theme';
import { DatePicker } from '@mui/x-date-pickers-v5';
import { TextField } from '@mui/material';
import { getDateFnsLocale } from '../../../../utils.js';

const DateInput = ({ inputRef, inputProps, InputProps, onFocus }) => (
  <TextField 
    {...inputProps}
    ref={inputRef}
    type="text"
    value={inputProps.value}
    size="small"
    className="d-block bg-white form-control w-100 h-100"
    onClick={onFocus}
    onKeyDown={onFocus}
    InputProps={{
      ...InputProps,
      readOnly: true,
      startAdornment: null,
      endAdornment: null,
      placeholder: ''
    }}
    sx={{
      ...inputProps.sx,
      '& .MuiInputBase-root': {
        width: '100%'
      },
      '& .MuiInputBase-input:not([disabled])': {
        cursor: 'pointer'
      }
    }}
  />
)

export default function GcDateRangePicker({ 
  slug = '',
  label = '',
  selectedDates,
  maxDate = null,
  minDate = null,
  disabled = false,
  error = false,
  parentFunction = null
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateRange, setDateRange] = useState({});

  useEffect(() => {
    setStartDate(selectedDates?.startDate ?? null);
    setEndDate(selectedDates?.endDate ?? null);
    setDateRange({
      startDate: selectedDates?.startDate ? new Date(selectedDates.startDate) : null,
      endDate: selectedDates?.endDate ? new Date(selectedDates.endDate) : null
    });
  }, [selectedDates]);

  const parseSqlDate = (date) => {
    if (!isNaN(Date.parse(date?.toISOString()))) {
      const day = ('0' + date.getDate()).slice(-2);
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      return date.getFullYear() + '-' + month + '-' + day;
    }
    return null;
  }

  const handleFocus = async (event) => {
    if (!anchorEl) {
      setAnchorEl(event.currentTarget.parentNode);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleStartDateChange = (value) => {
    if (value) {
      setOpen(true);
    }
  };

  const handleEndDateChange = (event) => {
    if (value) {
      setOpen(true);
    }
  };

  const handleChange = (range) => {
    const { startDate, endDate } = range;
    setDateRange({ startDate, endDate });
    const value = {
      startDate: parseSqlDate(startDate),
      endDate: parseSqlDate(endDate)
    };
    parentFunction(value, slug);
  };

  return (
    <>
      <ThemeProvider theme={backYouTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={ENV.lang.split('_').shift()}>
          {label != '' && <Label htmlForValue={"gc-daterange-picker-" + slug} label={label} />}
          <div className="row">
            <div className="col-12">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DatePicker
                  value={startDate}
                  disabled={disabled}
                  onChange={handleStartDateChange}
                  renderInput={(props) => <DateInput {...props} onFocus={handleFocus} />}
                  sx={{border: 'none'}}
                  className={`bg-white form-control ${error ? 'is-invalid' : ''}`}
                />
                <Box sx={{ margin: '0 5px' }}>
                  <i className="fa-regular fa-arrow-right"></i>
                </Box>
                <DatePicker
                  value={endDate}
                  disabled={disabled}
                  onChange={handleEndDateChange}
                  renderInput={(props) => <DateInput {...props} onFocus={handleFocus} />}
                  sx={{border: 'none'}}
                  className={`bg-white form-control ${error ? 'is-invalid' : ''}`}
                />
              </Box>
              <MuiDateRangePicker
                open={open}
                anchorEl={anchorEl}
                initialDateRange={dateRange}
                locale={getDateFnsLocale()}
                maxDate={maxDate}
                minDate={minDate}
                setStartDate={(startDate) => setStartDate(parseSqlDate(startDate))}
                setEndDate={(endDate) => setEndDate(parseSqlDate(endDate))}
                onChange={(range) => handleChange(range)}
                onClose={handleClose}
              />
            </div>
          </div>
        </LocalizationProvider>
      </ThemeProvider>
    </>
  );
}